import React from 'react';

const PivotTable = ({ 
  pivotData, 
  sortedRows, 
  colLabels, 
  displayOptions, 
  sampleSizes,
  selectedRowLabel,
  selectedColLabel,
  sortBy,
  selectSortBy
}) => {
  // Function to determine the heatmap color based on value and display options
  const getHeatmapColor = (value) => {
    if (displayOptions.calculationType === "zscore") {
      // Z-Score Display: Differentiated by positive/negative
      if (value >= 0) {
        const intensity = Math.min(value / 3, 1); // Cap at Z-score of 3
        return `rgba(0, 123, 255, ${0.3 + 0.7 * intensity})`; // Darker blue for positive Z-scores
      } else {
        const intensity = Math.min(Math.abs(value) / 3, 1); // Cap at Z-score of -3
        return `rgba(173, 216, 230, ${0.3 + 0.7 * intensity})`; // Lighter blue for negative Z-scores
      }
    } else if (displayOptions.calculationType === "rank") {
      // Rank Display: Scaling intensity by rank
      if (value === 0) {
        return `rgba(255, 255, 255, 1)`; // Pure white for rank 0
      }

      const maxRank = 10; // Adjust this if rank range varies
      const intensity = Math.min(1 - (value - 1) / maxRank, 1); // Inverse intensity for rank
      return `rgba(0, 123, 255, ${0.3 + 0.7 * intensity})`; // Blue gradient for ranks
    } else if (displayOptions.percentage) {
      // Percentage Display: Light to dark blue gradient
      const intensity = Math.min(value / 100, 1); // Cap percentage intensity at 100%
      return `rgba(0, 123, 255, ${0.3 + 0.7 * intensity})`; // Blue gradient for percentage
    } else {
      // Default Display (Raw Values): Light to dark gradient
      const maxRawValue = Math.max(...Object.values(pivotData).flatMap(row => Object.values(row))); // Determine max raw value dynamically
      const intensity = Math.min(value / maxRawValue, 1); // Scale intensity for raw values
      return `rgba(0, 123, 255, ${0.3 + 0.7 * intensity})`; // Unified blue gradient for raw values
    }
  };

  // Function to format the value based on display options
  const formatValue = (value) => {
    if (displayOptions.calculationType === "zscore") {
      return value.toFixed(2);
    } else if (displayOptions.calculationType === "rank") {
      return value;
    } else if (displayOptions.percentage) {
      return `${value.toFixed(1)}%`;
    } else {
      return value;
    }
  };

  return (
    <div className="pivot-table-section">
      <table className="pivot-table">
        <thead>
          <tr>
            <th
              onClick={() => selectSortBy('firstColumn')}
              style={{ cursor: 'pointer' }}
            >
              {selectedRowLabel} / {selectedColLabel}
              {sortBy.key === 'firstColumn' && (
                <i 
                  className={sortBy.order === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'}
                  style={{ marginLeft: '5px' }}
                ></i>
              )}
            </th>
            {colLabels.map((colLabel, index) => (
              <th 
                key={index} 
                title={colLabel}
                onClick={() => selectSortBy(colLabel)}
                style={{ cursor: 'pointer' }}
              >
                {colLabel}
                {sortBy.key === colLabel && (
                  <i 
                    className={sortBy.order === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'}
                    style={{ marginLeft: '5px' }}
                  ></i>
                )}
              </th>
            ))}
            {/* Add a header for row-wise sample sizes */}
            {displayOptions.percentage && displayOptions.percentageType === "row-wise" && (
              <th style={{ fontWeight: "bold" }}>Sample</th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length > 0 && (
            sortedRows.map((rowLabel, rowIndex) => (
              <tr key={rowIndex}>
                <td title={rowLabel}>{rowLabel}</td>
                {colLabels.map((colLabel, colIndex) => {
                  const value = pivotData[rowLabel]?.[colLabel] || 0;
                  return (
                    <td
                      key={colIndex}
                      style={{
                        backgroundColor: getHeatmapColor(value),
                        color: "#000000",
                      }}
                    >
                      {formatValue(value)}
                    </td>
                  );
                })}
                {/* Conditionally display the sample size column for rows */}
                {displayOptions.percentage &&
                  displayOptions.percentageType === "row-wise" && (
                    <td style={{ fontWeight: "bold" }}>{sampleSizes.rows[rowLabel] || 0}</td>
                  )}
              </tr>
            ))
          )}
          {/* Conditionally display the sample size row for columns */}
          {displayOptions.percentage && displayOptions.percentageType === "column-wise" && (
            <tr>
              <td style={{ fontWeight: "bold" }}>Sample</td>
              {colLabels.map((colLabel, colIndex) => (
                <td key={colIndex} style={{ fontWeight: "bold", textAlign: "center" }}>
                  {sampleSizes.columns[colLabel] || 0}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default PivotTable;
